import { decodeValue } from './encoding';
import { RouteParseError } from './RouteParseError';
import type { ParsedSelection, ParseOptions } from './types';

/** The reserved literal marking a skipped taxonomy level. */
const ALL = 'all';

/** The segment that separates the taxonomy part from the filter part. */
const SEPARATOR = '-';

/** Split a slash-delimited path into its non-empty segments. */
const toSegments = (path: string): string[] =>
  path.split('/').filter((segment) => segment.length > 0);

/** Decode a single value, converting a malformed percent-escape into a {@link RouteParseError}. */
const decodeOne = (value: string, path: string): string => {
  try {
    return decodeValue(value);
  } catch {
    throw new RouteParseError(`Malformed percent-encoding in segment "${value}"`, path);
  }
};

/** Decode a `;`-joined taxonomy segment into its list of decoded values. */
const decodeTaxonomyValues = (segment: string, path: string): string[] =>
  segment.split(';').map((value) => decodeOne(value, path));

/**
 * Parse a path-like product-discovery URL into a flat array of
 * {@link Selection}s.
 *
 * Behaviour:
 *  - When `taxonomyAliases` is non-empty the base path (per
 *    {@link ParseOptions.basePath}) is validated and stripped before any
 *    further parsing. When `taxonomyAliases` is empty, parsing operates in
 *    **filter-only mode**: the base path is not validated and everything up to
 *    and including the lone `-` separator segment is discarded.
 *  - The remaining segments are split at the segment that equals `-` *exactly*
 *    (not a substring split — slugs may contain `-` internally). Everything
 *    before the `-` is the taxonomy path; everything after is filter pairs.
 *    With no `-` segment, everything is taxonomy.
 *  - Taxonomy segments are mapped positionally onto
 *    {@link ParseOptions.taxonomyAliases}. A segment equal to the reserved
 *    `all` is a skipped level and is omitted from the output. Each segment is
 *    split on `;` into its (multi-select) value list.
 *  - Filter segments are consumed as `alias`/`value` pairs (one value per pair;
 *    filters are **not** split on `;`). Repeated occurrences of the same alias
 *    are merged into a single multi-value selection. An odd number of filter
 *    segments throws.
 *  - Every value is decoded: `+`→space first, then percent-decoding.
 *  - The path only is considered; anything from a `?` or `#` onward is ignored.
 *
 * The returned array lists taxonomy selections first (in path order), then
 * filter selections (in URL order). Each entry carries `part` to indicate its
 * origin.
 *
 * @throws {RouteParseError} if `taxonomyAliases` is non-empty and the input
 * does not start with the declared base path, has more taxonomy segments than
 * `taxonomyAliases` can name, or has a dangling filter pair.
 */
export const parse = (url: string, options: ParseOptions): ParsedSelection[] => {
  const { basePath, taxonomyAliases } = options;

  // Path only: discard the query string and/or fragment.
  const path = url.split(/[?#]/, 1)[0];

  let taxonomySegments: string[];
  let filterSegments: string[];

  if (taxonomyAliases.length > 0) {
    // basePath is validated and stripped only when taxonomy aliases are present.
    const startsAtBoundary = path === basePath || path.startsWith(`${basePath}/`);
    if (!startsAtBoundary) {
      throw new RouteParseError(`Path "${path}" does not start with base path "${basePath}"`, path);
    }
    const segments = toSegments(path.slice(basePath.length));
    // Split the taxonomy part from the filter part at the first lone `-` segment.
    const separatorIndex = segments.indexOf(SEPARATOR);
    taxonomySegments = separatorIndex === -1 ? segments : segments.slice(0, separatorIndex);
    filterSegments = separatorIndex === -1 ? [] : segments.slice(separatorIndex + 1);
  } else {
    // Filter-only mode: everything before the lone `-` separator is ignored —
    // there is no taxonomy to parse and basePath is not validated.
    const allSegments = toSegments(path);
    const separatorIndex = allSegments.indexOf(SEPARATOR);
    taxonomySegments = [];
    filterSegments = separatorIndex === -1 ? [] : allSegments.slice(separatorIndex + 1);
  }

  if (taxonomySegments.length > taxonomyAliases.length) {
    throw new RouteParseError(
      `Path has ${taxonomySegments.length} taxonomy segment(s) but only ${taxonomyAliases.length} alias(es) to name them`,
      path,
    );
  }

  const selections: ParsedSelection[] = [];

  taxonomySegments.forEach((segment, index) => {
    if (segment === ALL) {
      return; // skipped level — never surfaces to the caller
    }
    selections.push({
      alias: taxonomyAliases[index],
      values: decodeTaxonomyValues(segment, path),
      part: 'taxonomy',
    });
  });

  if (filterSegments.length % 2 !== 0) {
    throw new RouteParseError(
      `Path has a dangling filter pair: "${filterSegments.join('/')}"`,
      path,
    );
  }

  // Filters are `alias`/`value` pairs, one value per pair. Repeated occurrences
  // of the same alias are merged into a single multi-value selection.
  const filterByAlias = new Map<string, ParsedSelection>();
  for (let index = 0; index < filterSegments.length; index += 2) {
    const alias = filterSegments[index];
    const value = decodeOne(filterSegments[index + 1], path);
    const existing = filterByAlias.get(alias);
    if (existing) {
      existing.values.push(value);
    } else {
      const selection: ParsedSelection = { alias, values: [value], part: 'filter' };
      filterByAlias.set(alias, selection);
      selections.push(selection);
    }
  }

  return selections;
};
